// Podcast.tsx - Enhanced podcast page with episode listings, featured episodes,
// subscription links, and embedded players (Spotify and YouTube)

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import YouTubeEmbed from '../components/YouTubeEmbed';
import '../styles/Podcast.css';

// Import the images with fallback paths for production
import daveImage from '../assets/DaveAbbott.png';
import justinImage from '../assets/justin_cropped_taller.png';

// Fallback paths for production build - use process.env.PUBLIC_URL for proper deployment
const daveImageFallback = `${process.env.PUBLIC_URL}/assets/DaveAbbott.png`;
const justinImageFallback = `${process.env.PUBLIC_URL}/assets/justin_cropped_taller.png`;

const Podcast: React.FC = () => {
  // Featured episode sections
  const featuredEpisodeIds = ['latestEpisode', 'mostPopularEpisode'];
  const playerRef = useRef<HTMLDivElement>(null);
  const [spotifyLoaded, setSpotifyLoaded] = useState(false);

  // State for animation triggers
  const [visibleSections, setVisibleSections] = useState<{
    [key: string]: boolean;
  }>({
    spotifySection: false,
    youtubeSection: false,
    aboutHost: false,
    support: false,
  });

  // Intersection Observer for animation triggering
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          setVisibleSections((prev) => ({ ...prev, [sectionId]: true }));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const sections = [
      'spotify-section',
      'youtube-section',
      'about-host',
      'support-section',
    ];
    sections.forEach((section) => {
      const element = document.getElementById(section);
      if (element) observer.observe(element);
    });

    return () => {
      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element) observer.unobserve(element);
      });
    };
  }, []);

  // Handle Spotify iframe load detection
  useEffect(() => {
    const timer = setTimeout(() => {
      const iframe = document.querySelector('.spotify-embed-wrapper iframe') as HTMLIFrameElement;
      if (!iframe || !iframe.contentDocument) {
        setSpotifyLoaded(false);
        // Show fallback if iframe doesn't load
        const fallback = document.querySelector('.spotify-fallback') as HTMLElement;
        if (fallback) {
          fallback.style.display = 'flex';
        }
      } else {
        setSpotifyLoaded(true);
      }
    }, 3000); // Give iframe 3 seconds to load

    return () => clearTimeout(timer);
  }, []);

  const scrollToPlayer = (): void => {
    playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="podcast-container">
      {/* Podcast Header with Pub Sign Background */}
      <div className="podcast-header pub-sign-background">
        <div className="header-content">
          <h1>Sip'N'Security Podcast</h1>
          <p className="podcast-tagline">Where Bourbon Meets Cybersecurity</p>
          <p className="podcast-description">
            Join host Justin as he explores the fascinating parallels between
            premium bourbon craftsmanship and modern cybersecurity practices,
            featuring industry experts, master distillers, and in-depth
            discussions on how these two seemingly different worlds share common
            principles and values.
          </p>

          <div className="subscription-links">
            <a
              href="https://www.youtube.com/@sipnsecurity9497/featured"
              target="_blank"
              rel="noopener noreferrer"
              className="subscribe-button youtube"
            >
              <span className="platform-icon">📺</span> Watch on YouTube
            </a>
            <a
              href="https://open.spotify.com/show/2TDqcJbEoNkITEz23TkqNS"
              target="_blank"
              rel="noopener noreferrer"
              className="subscribe-button spotify"
            >
              <span className="platform-icon">🎧</span> Listen on Spotify
            </a>
          </div>
        </div>
      </div>

      {/* Spotify Podcast Player Section */}
      <div
        id="spotify-section"
        className={`spotify-section ${visibleSections.spotifySection ? 'visible' : ''}`}
        ref={playerRef}
      >
        <div className="section-background wooden-texture"></div>
        <h2>Listen to Our Latest Episodes</h2>
        <div className="spotify-container">
          {/* Working Spotify embed with fallback */}
          <div className="spotify-embed-wrapper">
            <iframe
              src="https://open.spotify.com/embed/show/2TDqcJbEoNkITEz23TkqNS?utm_source=generator&theme=0"
              width="100%"
              height="452"
              frameBorder="0"
              allowTransparency={true}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              title="Sip'N'Security Podcast on Spotify"
              style={{
                minHeight: "452px",
                border: "none",
                borderRadius: "12px",
                display: "block",
                backgroundColor: "#1DB954"
              }}
            ></iframe>
          </div>

          {/* Fallback content if iframe doesn't load */}
          <div className="spotify-fallback" style={{ display: "none" }}>
            <div className="fallback-content">
              <div className="fallback-icon">🎧</div>
              <h3>Listen to Sip'N'Security Podcast</h3>
              <p>Click below to listen to all our episodes on Spotify</p>
              <a
                href="https://open.spotify.com/show/2TDqcJbEoNkITEz23TkqNS"
                target="_blank"
                rel="noopener noreferrer"
                className="fallback-link"
              >
                Open Spotify Player
              </a>
            </div>
          </div>
        </div>

        <div className="spotify-description">
          <p>
            <strong>Play episodes directly above!</strong> The embedded player allows you to listen to full episodes, 
            browse our catalog, and follow the podcast - all without leaving our website.
          </p>
          <div className="listen-buttons">
            <button className="listen-now-button" onClick={scrollToPlayer}>
              <span className="listen-icon">🎧</span> Play in Embedded Player
            </button>
            <button className="listen-now-button secondary" onClick={() => {
              window.open('https://open.spotify.com/show/2TDqcJbEoNkITEz23TkqNS', '_blank');
            }}>
              <span className="listen-icon">📱</span> Open in Spotify App
            </button>
          </div>
        </div>
      </div>

      {/* YouTube Channel Section */}
      <div
        id="youtube-section"
        className="youtube-section visible"
      >
        <div className="section-background barrel-texture"></div>
        <h2>Watch Our Videos on YouTube</h2>
        <div className="youtube-container">
          <div className="youtube-direct-link-block">
            <a
              href="https://www.youtube.com/@sipnsecurity9497/featured"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-direct-link main"
            >
              <span className="youtube-icon">📺</span> Visit Our YouTube Channel
            </a>
            <p className="youtube-link-description">
              Explore all our latest podcast videos, bourbon reviews, and cybersecurity content directly on our official YouTube channel.
            </p>
          </div>
        </div>
      </div>

      {/* Support the Show Section */}
      <div
        id="support-section"
        className={`support-section ${
          visibleSections.support ? 'visible' : ''
        }`}
      >
        <div className="section-background bourbon-bottles"></div>
        <h2>Support the Show</h2>
        <div className="support-options">
          <div className="support-card">
            <div className="support-icon">🛍️</div>
            <h3>Shop Merchandise</h3>
            <p>
              Show your love for Sip'N'Security with our premium bourbon
              glassware, apparel, and accessories. Each purchase directly
              supports the production of new episodes.
            </p>
            <Link to="/store" className="support-link">
              Visit the Store
            </Link>
          </div>
          <div className="support-card">
            <div className="support-icon">🥃</div>
            <h3>Join The Whiskey Club</h3>
            <p>
              Subscribe to our premium membership for exclusive bourbon tasting notes, behind-the-scenes
              content, early episode access, and virtual tasting events. Connect with fellow
              security-minded bourbon enthusiasts.
            </p>
            <Link to="/store" className="support-link">
              Become a Member
            </Link>
          </div>
          <div className="support-card">
            <div className="support-icon">🔄</div>
            <h3>Share with Friends</h3>
            <p>
              The best way to support us is to share episodes with fellow
              bourbon lovers and security professionals. Help spread the word
              about our unique approach.
            </p>
            <div className="share-buttons">
              <a
                href="https://twitter.com/intent/tweet?text=Check out the Sip'N'Security podcast!&url=https://sipnsecurity.com"
                target="_blank"
                rel="noopener noreferrer"
                className="share-button twitter"
              >
                Twitter
              </a>
              <a
                href="https://www.facebook.com/sharer/sharer.php?u=https://sipnsecurity.com"
                target="_blank"
                rel="noopener noreferrer"
                className="share-button facebook"
              >
                Facebook
              </a>
              <a
                href="https://www.linkedin.com/sharing/share-offsite/?url=https://sipnsecurity.com"
                target="_blank"
                rel="noopener noreferrer"
                className="share-button linkedin"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Podcast;